<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.gamification_chests', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex justify-between items-center">
            <div class="flex space-x-2 items-center">
                <NavLinks :links="meta.navGroupLinks" />

                <NavLinks :links="meta.navDaysLinks" />
            </div>

            <AppButton as="inertia-link" :href="meta.links.edit_chest_settings">
                {{ $t('messages.settings') }}
            </AppButton>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date')" for="date" />
                        <FormInput required id="date" v-model="form.date" name="date" type="date" class="w-full mt-1" />
                        <FormError :value="form.errors.date" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <EmptyState v-if="chests.length === 0" picture />
        <div v-else class="flex flex-col mt-5">
            <div>
                {{ $t('messages.opened') }}: {{ stats.open_count }} / {{ stats.total_count }} ({{
                    stats.open_percent
                }}%)
            </div>

            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 mt-5">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.status') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.type') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.gamification_rewards', 2) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(chest, index) in chests"
                                :key="chest.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <a :href="chest.student.url" class="text-indigo-500">
                                        {{ chest.student.full_name }}
                                    </a>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-2 items-center">
                                        <BadgePill :color="getStatusColor(chest.status)">
                                            {{ $t('messages.' + chest.status) }}
                                        </BadgePill>
                                        <div>
                                            {{ chest.open_ago }}
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <span
                                        :class="{
                                            'text-gray-300': chest.type === 'default',
                                        }"
                                    >
                                        {{ chest.type_display }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-2 items-center">
                                        <BadgePill color="yellow" v-if="chest.coins_amount">
                                            {{ chest.coins_amount }}
                                        </BadgePill>
                                        <BadgePill color="green" v-if="chest.experience_amount">
                                            {{ chest.experience_amount }}
                                        </BadgePill>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import BadgePill from '@/components/BadgePill.vue';

import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import NavLinks from '@/components/NavLinks.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        EmptyState,
        PanelPage,
        FormInput,
        FormLabel,
        FormError,
        BadgePill,
        AppButton,
        NavLinks,
    },
    props: ['meta', 'filters', 'gamificationSet', 'chests', 'groups', 'stats'],
    layout: Layout,
    data() {
        return {
            form: this.$inertia.form({
                date: this.filters.date,
                group_id: this.filters.group_id,
            }),
        };
    },
    name: 'Index',
    computed: {},
    methods: {
        getStatusColor(status) {
            if (status === 'closed') {
                return 'gray';
            }
            if (status === 'opened') {
                return 'green';
            }
        },
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
